import React from "react";
import { useTranslation } from "react-i18next";
import { DataCell } from "@/components/DataCell";
import { PercentChange } from "@/components/DataCell/PercentChange";
import { Users, MessageCircle, Calendar1, Mail } from "lucide-react";
import { useContextMetrics } from "@/hooks/useContextMetrics";
import { useOrg } from "@/hooks/useOrg";
import { OrganizationMetricsContextEnum } from "@/__generated__/types";
import { CircleIcon } from "@/components/DashboardOverviewDataCells/CircleIcon";

export const GamesAndLiveEventsBento: React.FC = () => {
  const { t } = useTranslation();
  const { org } = useOrg();
  const {
    loading: metricsLoading,
    error: metricsError,
    getPreviousCycleValue,
    getMetricDisplayValue,
    getMetricCalculatedPercentChanged,
  } = useContextMetrics(org?.id, OrganizationMetricsContextEnum.LiveEvents);

  const hasRequiredData = Boolean(org?.id);
  const hasError = metricsError || (!hasRequiredData && !metricsLoading);

  const showLoading = () => {
    if (hasError) return false;
    return metricsLoading;
  };

  const showError = () => {
    return hasError;
  };

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("home.overview.dataCells.error.loadingError")}
    </span>
  );

  return (
    <div>
      {/* Title Section */}
      <h2 className="mb-2 text-sm leading-5 font-normal text-muted-foreground">
        {t("gamesAndLiveEvents.bento.title")}
      </h2>

      {/* Data Cells Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Events DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("gamesAndLiveEvents.bento.totalEvents")}
          tooltipText={t("gamesAndLiveEvents.bento.totalEventsToolTips")}
          middleValue={
            showError() ? errorContent : getMetricDisplayValue("totalEvents")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("totalEvents")}
                prevCount={getPreviousCycleValue("totalEvents")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="indigo">
              <Calendar1 />
            </CircleIcon>
          }
        />

        {/* Total Participants DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("gamesAndLiveEvents.bento.totalParticipants")}
          tooltipText={t("gamesAndLiveEvents.bento.totalParticipantsToolTips")}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("totalParticipants")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("totalParticipants")}
                prevCount={getPreviousCycleValue("totalParticipants")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="green">
              <Users />
            </CircleIcon>
          }
        />

        {/* Total Messages DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("gamesAndLiveEvents.bento.totalMessages")}
          tooltipText={t("gamesAndLiveEvents.bento.totalMessagesToolTips")}
          middleValue={
            showError() ? errorContent : getMetricDisplayValue("totalMessages")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("totalMessages")}
                prevCount={getPreviousCycleValue("totalMessages")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="violet">
              <Mail />
            </CircleIcon>
          }
        />

        {/* Total Channel Views DataCell */}
        <DataCell
          className="min-h-[142px]"
          isLoading={showLoading()}
          title={t("gamesAndLiveEvents.bento.totalChannelViews")}
          tooltipText={t("gamesAndLiveEvents.bento.totalChannelViewsToolTips")}
          middleValue={
            showError()
              ? errorContent
              : getMetricDisplayValue("totalChannelViews")
          }
          bottomValue={
            showError() ? null : (
              <PercentChange
                value={getMetricCalculatedPercentChanged("totalChannelViews")}
                prevCount={getPreviousCycleValue("totalChannelViews")}
                customTemplate={t("metrics.comparisons.yearOverYear", {
                  percentChanged: "{{percentChange}}",
                })}
              />
            )
          }
          icon={
            <CircleIcon color="orange">
              <MessageCircle />
            </CircleIcon>
          }
        />
      </div>
    </div>
  );
};
